import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Download, Trash2, ShieldCheck, Mail, UserCog, FileText, ExternalLink, Check, Bell,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { PageHeader, SectionCard, Spinner } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function PrivacyCenter() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [consents, setConsents] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get("/account/consents").then(({ data }) => setConsents(data)).catch(() => setConsents({}));
  }, []);

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await api.get("/account/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "compeni-my-data.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("privacy.toastExport"));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setExporting(false);
    }
  };

  const toggleMarketing = async (val) => {
    try {
      await api.patch("/account/consents", { marketing: val });
      setConsents((c) => ({ ...c, marketing: { ...(c?.marketing || {}), value: val, changed_at: new Date().toISOString() } }));
      toast.success(val ? t("privacy.toastSubscribed") : t("privacy.toastUnsubscribed"));
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleNetworkEmail = async (val) => {
    try {
      await api.patch("/account/notifications", { email_network: val });
      setConsents((c) => ({ ...c, notifications: { ...(c?.notifications || {}), email_network: val } }));
      toast.success(val ? t("privacy.toastNetworkOn") : t("privacy.toastNetworkOff"));
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/account");
      toast.success(t("privacy.toastDeleted"));
      setUser(false);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
      setDeleting(false);
    }
  };

  const editLink = user?.role === "expert" ? "/expert/profile" : null;
  const marketingOn = !!consents?.marketing?.value;
  const networkEmailOn = consents?.notifications?.email_network !== false;

  return (
    <AppShell>
      <PageHeader eyebrow={t("privacy.eyebrow")} title={t("privacy.title")} description={t("privacy.subtitle")} />

      {consents === null ? (
        <div className="grid place-items-center py-24"><Spinner className="text-primary w-7 h-7" /></div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          {/* Consents */}
          <SectionCard icon={ShieldCheck} title={t("privacy.consentTitle")} description={t("privacy.consentDesc")} className="animate-fade-up d1">
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4 rounded-xl border border-border p-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{t("privacy.dataProcessing")}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t("privacy.dataProcessingDesc")}</p>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-success shrink-0" data-testid="consent-privacy-status"><Check size={14} /> {t("privacy.granted")}</span>
              </div>
              <div className="flex items-start justify-between gap-4 rounded-xl border border-border p-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{t("privacy.marketing")}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t("privacy.marketingDesc")}</p>
                </div>
                <Switch data-testid="consent-marketing-toggle" checked={marketingOn} onCheckedChange={toggleMarketing} />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-4">{t("privacy.policyVersion", { version: consents?.policy_version || "—" })}</p>
          </SectionCard>

          {/* Notification preferences */}
          <SectionCard icon={Bell} title={t("privacy.notifTitle")} description={t("privacy.notifDesc")} className="animate-fade-up d1">
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4 rounded-xl border border-border p-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{t("privacy.networkInvites")}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t("privacy.networkInvitesDesc")}</p>
                </div>
                <Switch data-testid="notif-network-toggle" checked={networkEmailOn} onCheckedChange={toggleNetworkEmail} />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-4">{t("privacy.notifFooter")}</p>
          </SectionCard>

          {/* Access / rectification */}
          <SectionCard icon={Download} title={t("privacy.accessTitle")} description={t("privacy.accessDesc")} className="animate-fade-up d2">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("privacy.accessBody")}
            </p>
            <Button data-testid="export-data-btn" onClick={exportData} disabled={exporting} className="rounded-lg gap-1.5 mt-4">
              {exporting ? <Spinner /> : <><Download size={16} /> {t("privacy.exportBtn")}</>}
            </Button>

            <div className="mt-6 pt-6 border-t border-border">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground"><UserCog size={16} className="text-primary" /> {t("privacy.correctTitle")}</div>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                {editLink ? t("privacy.correctExpert") : t("privacy.correctOther")}
              </p>
              {editLink && (
                <Link to={editLink}><Button variant="outline" size="sm" className="rounded-lg gap-1.5 mt-3 bg-card" data-testid="rectify-link"><FileText size={15} /> {t("privacy.editProfile")}</Button></Link>
              )}
            </div>
          </SectionCard>

          {/* Policy */}
          <SectionCard icon={Mail} title={t("privacy.policyTitle")} description={t("privacy.policyDesc")} className="animate-fade-up d3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("privacy.policyBody")}
            </p>
            <Link to="/privacy"><Button variant="outline" className="rounded-lg gap-1.5 mt-4 bg-card" data-testid="view-policy-btn"><ExternalLink size={15} /> {t("privacy.viewPolicy")}</Button></Link>
          </SectionCard>

          {/* Erasure */}
          <SectionCard icon={Trash2} title={t("privacy.deleteTitle")} description={t("privacy.deleteDesc")} className="animate-fade-up d4 border-destructive/30">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("privacy.deleteBody")}
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="delete-account-btn" variant="outline" className="rounded-lg gap-1.5 mt-4 text-destructive border-destructive/40 hover:bg-destructive/5 hover:text-destructive">
                  <Trash2 size={16} /> {t("privacy.deleteBtn")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-[0.8rem]">
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("privacy.deleteConfirmTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("privacy.deleteConfirmDesc")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-lg" data-testid="delete-cancel">{t("privacy.keepAccount")}</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="delete-confirm"
                    onClick={(e) => { e.preventDefault(); deleteAccount(); }}
                    disabled={deleting}
                    className="rounded-lg bg-destructive hover:bg-destructive/90"
                  >
                    {deleting ? t("privacy.deleting") : t("privacy.deletePermanent")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </SectionCard>
        </div>
      )}
    </AppShell>
  );
}
